import React, { useEffect, useMemo, useState } from 'react';
import styled from "styled-components";
import Colours from '@shared/ui/atoms/Colours';
import DateFlicker, { DateScale } from '@shared/ui/molecules/DateFlicker';
import moment, { Moment } from "moment";
import { getWhereaboutsSummary } from '@features/reporting/services/ReportingService';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { Center } from '@shared/ui/atoms/StructuralLayout';
import { MountedContentCard, MountedContentCardContent } from '@shared/ui/atoms/MountedContentCard';
import { BodyLarge, BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import { WhereaboutsSummaryBarChart } from "./components/WhereaboutsSummaryBarChart";
import { WhereaboutsOption } from '@features/whereabouts/services/WhereaboutOptions';
import TeamDropDownList, { ALL_TEAMS } from '@shared/ui/organisms/team-drop-down-list/TeamDropDownList';
import { useSelector } from "react-redux";
import { WhereaboutsBookingDownloadReportBar } from "./components/WhereaboutsBookingDownloadReportBar";
import { Spacer } from '@shared/ui/atoms/VerticalSpacer';
import { ToggleableOutlineButton } from '@shared/ui/atoms/buttons/ToggleableOutlineButton';
import { HeadlineLarge } from '@shared/ui/atoms/fonts/Headline';
import { useTranslation } from "react-i18next";
import { selectAllTeams } from "@shared/store/config.duck";
import { selectCurrentUser } from "@shared/store/auth.duck";
import {
  isBasicUser,
  isCompanyAdminUser,
  isDepartmentAdmin,
  isOfficeAdminUser,
  isTeamAdminUser
} from "@shared/utils/AccessControl";
import { AccessToReportDenied } from "./components/AccessToReportDenied";
import { selectAllDepartments } from '@features/whereabouts/pages/departments/data/departments.duck';
import { DepartmentWhereaboutsReport } from "./DepartmentWhereaboutsReport";
import { WorkGroupWhereaboutsReport } from "./WokrGroupWhereaboutsReport";
import { getParameterByName } from "@shared/utils/UrlUtils";
import { tablet } from '@shared/ui/atoms/MediaQueries';

enum View {
  TeamReport, DepartmentReport, WorkgroupReport
}

export function transformWhereaboutsUsage(optionsList: WhereaboutsOption[], usage: any) {
  let result = {
  };
  Object.keys(usage).forEach(k => {
    const option = optionsList.find(wo => wo.key === k);
    // @ts-ignore
    if (option) {
      // @ts-ignore
      result[option.label] = usage[k];
    }
  })
  return result;
}

export function calculateMaxValue(data: any) {
  let highestValue = 0;
  Object.values(data).forEach((row: any) => {
    const sum = Object.values(row).reduce((a: any, b: any) => {
      return a + (typeof b === 'number' ? b : 0);
    }, 0) as number;
    if (sum > highestValue) highestValue = sum;
  })
  return highestValue;
}

export function WhereaboutsReportingPage(props: Props) {
  const currentUser = useSelector(selectCurrentUser);
  const [date, setDate] = useState<Moment>(moment());
  const [teamId, setTeamId] = useState<string | undefined>(undefined);
  const [teamName, setTeamName] = useState<string>('');
  const [reportData, setReportData] = useState<any>([]);
  const [keyTableData, setKeyTableData] = useState<any>([]);
  const [maxYAxisValue, setMaxYAxisValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState(View.TeamReport);
  const teams = useSelector(selectAllTeams);
  const {t} = useTranslation();
  const canUserViewAllTeams = isCompanyAdminUser(currentUser) || isOfficeAdminUser(currentUser);
  const isPageDisabled = isBasicUser(currentUser);
  const departments = useSelector(selectAllDepartments);
  const canUserViewDepartmentReport = isCompanyAdminUser(currentUser) || isDepartmentAdmin(currentUser);

  const visibleTeams = useMemo(() => {
    if (isCompanyAdminUser(currentUser) || isOfficeAdminUser(currentUser)) {
      return teams;
    } else if ((isDepartmentAdmin(currentUser) || isTeamAdminUser(currentUser)) && currentUser?.teamEntity) {
      return teams.filter(t => t.id === currentUser.teamEntity.id);
    } else {
      return [];
    }
  }, [teams, currentUser]);

  useEffect(() => {
    if (visibleTeams.length === 1 && !canUserViewAllTeams) {
      setTeamId(visibleTeams[0].id);
      setTeamName(visibleTeams[0].name);
    } else if (canUserViewAllTeams) {
      setTeamId(ALL_TEAMS)
      setTeamName('All teams')
    }
  }, [canUserViewAllTeams, visibleTeams]);

  useEffect(() => {
    const urlView = getParameterByName('view', window.location.href);
    if (urlView === 'team') setActiveView(View.TeamReport);
    else if (urlView === 'department') setActiveView(View.DepartmentReport);
    else if (urlView === 'workgroup') setActiveView(View.WorkgroupReport);
  }, []);


  const onTeamIdSelected = (teamId: string) => {
    setTeamId(teamId)
    if (teamId && teamId !== ALL_TEAMS) {
      setTeamName(visibleTeams.find(t => t.id === teamId)?.name ?? '');
    } else if (teamId === ALL_TEAMS) {
      setTeamName('All teams');
    } else {
      setTeamName('');
    }
  }

  const onDateChange = (date: Moment) => {
    setDate(date);
  }

  useEffect(() => {
    if (date && teamId) {
      setReportData([]);
      setLoading(true)
      getWhereaboutsSummary(date, teamId).then(resp => {
        const optionList = resp.whereaboutsOptionList;
        const whereaboutsMonthlySummaryKeys = Object.keys(resp.whereaboutsUsage);
        const data = whereaboutsMonthlySummaryKeys.map(key => ({
          name: moment(key).format('ddd')[0],
          dateOfMonth: moment(key).format('DD'),
          date: key,
          // @ts-ignore
          ...transformWhereaboutsUsage(optionList, resp.whereaboutsUsage[key])
        }))
        data.sort((a, b) => a.date < b.date ? -1 : 1);
        setReportData(data);
        setKeyTableData(optionList);
        setLoading(false);
        setMaxYAxisValue(calculateMaxValue(resp.whereaboutsUsage));
      })
    }
  }, [date, teamId]);

  if (isPageDisabled) {
    return <AccessToReportDenied />
  }

  return (
    <ReportingContainer>
      <HeaderRow>
        <GoBackButton url={"/manage/reporting"} />
        <ControlButtons>
          {departments.length > 0 && canUserViewDepartmentReport && <ToggleableOutlineButton isActive={activeView === View.DepartmentReport}
                                                                                             click={() => setActiveView(View.DepartmentReport)}
                                                                                             text={'button.department-report'} />}
          <ToggleableOutlineButton isActive={activeView === View.TeamReport}
                                   click={() => setActiveView(View.TeamReport)}
                                   text={'button.team-report'} />
          <ToggleableOutlineButton isActive={activeView === View.WorkgroupReport}
                                   click={() => setActiveView(View.WorkgroupReport)}
                                   text={'button.workgroup-report'} />
          <ToggleableOutlineButton link={"/manage/user/whereabouts-summary"}
                                   isActive={false}
                                   text={'button.user-report'} />
        </ControlButtons>
      </HeaderRow>
      {activeView === View.DepartmentReport && canUserViewDepartmentReport && <>
        <DepartmentWhereaboutsReport />
      </>}
      {activeView === View.TeamReport && <>

        <HeadlineLarge style={{marginBottom: 16}}>{t('reports-page.whereabouts-title')}</HeadlineLarge>

        <FilterAndViewControlBar>
          <DateFlicker dateScale={DateScale.Month}
                       updateGlobalDate={false}
                       onChange={onDateChange}
                       disabled={loading} />
          <div>
            <TeamDropDownList teams={visibleTeams}
                              selectedTeamId={teamId}
                              onSelect={onTeamIdSelected}
                              includeAllTeams={canUserViewAllTeams} />
          </div>
        </FilterAndViewControlBar>
        {!!teamId ? <>
          {loading ? <>
            <Center>
              <LoadingSpinner hideBorder={true} />
            </Center>
          </> : <>
            {reportData.length === 0 ? <>
              <Center style={{marginTop: 32}}>
                <MountedContentCard>
                  <MountedContentCardContent>
                    <BodyLarge weight={600} colour={Colours.blue} style={{marginBottom: 12}}>{t('reports-page.whereabouts-page.no-date')}</BodyLarge>
                    <BodyRegular>{t('reports-page.whereabouts-page.select-a-date')}</BodyRegular>
                  </MountedContentCardContent>
                </MountedContentCard>
              </Center>
            </> : <>
              <WhereaboutsSummaryBarChart reportData={reportData}
                                          maxValueOfYAxis={maxYAxisValue}
                                          keyTableData={keyTableData} />
            </>}
          </>}
          <Spacer />
          <WhereaboutsBookingDownloadReportBar selectedTeamId={teamId} selectedTeamName={teamName} />
        </> : <>
        </>}
      </>}

      {activeView === View.WorkgroupReport && <>
        <WorkGroupWhereaboutsReport />
      </>}
    </ReportingContainer>
  );
}

const ReportingContainer = styled.div`
  max-width: 100%;
  min-height: 100vh;
  background-color: white;
  .recharts-wrapper .recharts-cartesian-grid-horizontal line {
    stroke-opacity: 0;
  }
  .recharts-cartesian-axis-tick-line {
    display: none !important;
  }
  @media (${tablet}) {
    padding: 24px;
  }
`

const HeaderRow = styled.div`
  display: flex;
  margin-bottom: 24px;
  flex-direction: column;
  @media (${tablet}) {
    flex-direction: row;
  }
  .goBackButton {
    align-self: flex-start;
    margin: 16px 0 16px 0;
    @media (${tablet}) {
      align-self: center;
    }
  }
`

const ControlButtons = styled.div`
  display: flex;
  align-self: center;
  justify-self: center;
  margin-left: auto;
  margin-right: auto;
  max-width: 100%;
  overflow-x: auto;
`

const FilterAndViewControlBar = styled.div`
  display: flex;
  flex-direction: column;
  .weekFlicker {
    margin-bottom: 12px;
  }
  @media (${tablet}) {
    justify-content: space-between;
    flex-direction: row;
    .weekFlicker {
      margin-bottom: 0;
    }
  }
`

interface Props {
}

